/**
 * Sort utilities for client-side ad sorting.
 *
 * Provides type-safe sorting functions with fallback logic for missing fields.
 */

import type { Ad } from "@/components/AdCard";

/**
 * Available sort options for ad grids.
 *
 * - `shuffle`: Random shuffle (Fisher-Yates algorithm, new order each call)
 * - `newest`: Sort by created_at descending (most recent first)
 * - `oldest`: Sort by created_at ascending (earliest first)
 * - `longest`: Sort by duration/runtime if available, fallback to created_at
 * - `impressions`: Sort by impression count if available, fallback to created_at
 */
export type SortOption = "shuffle" | "newest" | "oldest" | "longest" | "impressions";

/**
 * Extended ad interface with optional fields that may be present
 * from various data sources (API, database views, etc.)
 *
 * Note: The base Ad interface already includes `start` and `end` as required fields.
 */
export interface SortableAd extends Ad {
  createdAt?: string;
  created_at?: string;
  duration?: number;
  runtime?: number;
  impressions?: number;
  impression_count?: number;
  __seq?: number;
}

/**
 * Extracts the created date from an ad object.
 * Handles multiple possible field names for compatibility.
 */
function getCreatedDate(ad: SortableAd): Date {
  // Try createdAt first (camelCase from frontend transformations)
  if (ad.createdAt) {
    const date = new Date(ad.createdAt);
    if (!isNaN(date.getTime())) return date;
  }

  // Try created_at (snake_case from database)
  if (ad.created_at) {
    const date = new Date(ad.created_at);
    if (!isNaN(date.getTime())) return date;
  }

  // Fallback to start date if available
  if (ad.start) {
    const date = new Date(ad.start);
    if (!isNaN(date.getTime())) return date;
  }

  // Default to epoch (puts items without dates at the end)
  return new Date(0);
}

/**
 * Extracts the duration from an ad object.
 * Calculates from start/end dates if explicit duration is not available.
 *
 * @returns Duration in days, or null if not calculable
 */
function getDuration(ad: SortableAd): number | null {
  // Try explicit duration field first
  if (typeof ad.duration === "number" && ad.duration > 0) {
    return ad.duration;
  }

  // Try runtime field (alternate naming)
  if (typeof ad.runtime === "number" && ad.runtime > 0) {
    return ad.runtime;
  }

  // Calculate from start/end dates if both are present
  if (ad.start && ad.end) {
    const startDate = new Date(ad.start);
    const endDate = new Date(ad.end);

    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      const durationMs = endDate.getTime() - startDate.getTime();
      const durationDays = durationMs / (1000 * 60 * 60 * 24);

      if (durationDays > 0) {
        return durationDays;
      }
    }
  }

  // Calculate from start date to now (for currently running ads)
  if (ad.start) {
    const startDate = new Date(ad.start);
    if (!isNaN(startDate.getTime())) {
      const now = new Date();
      const durationMs = now.getTime() - startDate.getTime();
      const durationDays = durationMs / (1000 * 60 * 60 * 24);

      if (durationDays > 0) {
        return durationDays;
      }
    }
  }

  return null;
}

/**
 * Extracts the impression count from an ad object.
 *
 * @returns Impression count, or null if not available
 */
function getImpressions(ad: SortableAd): number | null {
  // Try impressions field first
  if (typeof ad.impressions === "number" && ad.impressions > 0) {
    return ad.impressions;
  }

  // Try impression_count field (alternate naming)
  if (typeof ad.impression_count === "number" && ad.impression_count > 0) {
    return ad.impression_count;
  }

  return null;
}

/**
 * Sorts an array of ads based on the specified sort option.
 *
 * This function creates a new sorted array without mutating the original.
 * For `longest` and `impressions` sorts, items without the relevant data
 * are placed at the end, sorted by created date.
 *
 * @param ads - Array of ads to sort
 * @param sortBy - The sort option to apply
 * @returns A new sorted array of ads
 *
 * @example
 * ```tsx
 * const sorted = sortAds(adList, 'newest');
 * ```
 */
export function sortAds<T extends SortableAd>(
  ads: T[],
  sortBy: SortOption
): T[] {
  // Create a shallow copy to avoid mutating the original array
  const sorted = [...ads];

  switch (sortBy) {
    case "shuffle":
      // Fisher-Yates shuffle — new random order each time
      for (let i = sorted.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
      }
      return sorted;

    case "newest":
      return sorted.sort((a, b) => {
        const dateA = getCreatedDate(a);
        const dateB = getCreatedDate(b);
        return dateB.getTime() - dateA.getTime();
      });

    case "oldest":
      return sorted.sort((a, b) => {
        const dateA = getCreatedDate(a);
        const dateB = getCreatedDate(b);
        return dateA.getTime() - dateB.getTime();
      });

    case "longest":
      return sorted.sort((a, b) => {
        const durationA = getDuration(a);
        const durationB = getDuration(b);

        // Both have duration: sort by duration descending
        if (durationA !== null && durationB !== null) {
          return durationB - durationA;
        }

        // Only one has duration: prioritize the one with duration
        if (durationA !== null) return -1;
        if (durationB !== null) return 1;

        // Neither has duration: fallback to newest first
        const dateA = getCreatedDate(a);
        const dateB = getCreatedDate(b);
        return dateB.getTime() - dateA.getTime();
      });

    case "impressions":
      return sorted.sort((a, b) => {
        const impressionsA = getImpressions(a);
        const impressionsB = getImpressions(b);

        // Both have impressions: sort by impressions descending
        if (impressionsA !== null && impressionsB !== null) {
          return impressionsB - impressionsA;
        }

        // Only one has impressions: prioritize the one with impressions
        if (impressionsA !== null) return -1;
        if (impressionsB !== null) return 1;

        // Neither has impressions: fallback to newest first
        const dateA = getCreatedDate(a);
        const dateB = getCreatedDate(b);
        return dateB.getTime() - dateA.getTime();
      });

    default:
      // Exhaustive check: if a new sort option is added but not handled,
      // TypeScript will catch it at compile time
      const _exhaustiveCheck: never = sortBy;
      return sorted;
  }
}

/**
 * Display labels for sort options.
 * Use these when rendering UI elements for sort selection.
 */
export const SORT_OPTION_LABELS: Record<SortOption, string> = {
  shuffle: "Shuffle",
  newest: "Newest",
  oldest: "Oldest",
  longest: "Longest Running",
  impressions: "Most Impressions",
};

/**
 * All available sort options as an array.
 * Useful for iterating when building UI elements.
 */
export const SORT_OPTIONS: SortOption[] = [
  "shuffle",
  "newest",
  "oldest",
  "longest",
  "impressions",
];

/**
 * Default sort option for new page loads.
 */
export const DEFAULT_SORT_OPTION: SortOption = "shuffle";
